// bm-design-system: checkbox primitive
import * as React from "react";
import { cn } from "@/lib/utils";

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, ...props }, ref) => {
    return (
      <input
        ref={ref}
        type="checkbox"
        className={cn(
          "h-4 w-4 shrink-0 rounded-md border border-hairline bg-page text-accent",
          "accent-[var(--color-accent)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-page",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);
Checkbox.displayName = "Checkbox";

export interface CheckboxOptionProps extends Omit<CheckboxProps, "children"> {
  /** The option's own words. Clicking them toggles the box. */
  label: React.ReactNode;
  /** Helper text beneath the label; also part of the clickable area. */
  description?: React.ReactNode;
  /** Controls beside the label that must not toggle it on click. */
  children?: React.ReactNode;
}

/**
 * A checkbox with its words — the same shape as RadioOption, for the same
 * reason: the box and its words toggle it, the blank space beyond them does
 * not.
 */
const CheckboxOption = React.forwardRef<HTMLInputElement, CheckboxOptionProps>(
  ({ className, label, description, children, ...props }, ref) => (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
      <label className="inline-flex min-w-0 items-start gap-2 text-sm font-normal text-ink-body">
        <Checkbox ref={ref} className="mt-0.5" {...props} />
        <span className="min-w-0">
          {label}
          {description && (
            <span className="block text-xs text-ink-muted">{description}</span>
          )}
        </span>
      </label>
      {children}
    </div>
  ),
);
CheckboxOption.displayName = "CheckboxOption";

export { Checkbox, CheckboxOption };
